import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ColorPickerPopover } from './ColorPickerPopover';
import { ThemePreview } from './ThemePreview';

interface ComponentInfo {
  id: string;
  name: string;
  component: any;
}

interface ThemeManagerControlsProps {
  selectedTheme: string;
  onThemeChange: (theme: string) => void;
  availableThemes: any[];
  selectedComponent: string;
  onComponentChange: (component: string) => void;
  components: ComponentInfo[];
  componentStyles: Record<string, string>;
  onColorChange: (component: string, property: string, color: string) => void;
}

export const ThemeManagerControls: React.FC<ThemeManagerControlsProps> = ({
  selectedTheme,
  onThemeChange,
  availableThemes,
  selectedComponent,
  onComponentChange,
  components,
  componentStyles,
  onColorChange,
}) => {
  return (
    <>
      {/* Theme Selector - Grid Layout with Preview Widgets */}
      <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
        <label className="block text-sm font-medium text-gray-700 mb-4">Select Theme</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {(availableThemes || []).map(theme => (
            <div
              key={theme.id}
              onClick={() => onThemeChange(theme.id)}
              className={cn(
                'relative border rounded-lg p-3 cursor-pointer transition-all',
                selectedTheme === theme.id
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              )}
            >
              {/* Selection checkmark */}
              {selectedTheme === theme.id && (
                <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1 z-10">
                  <Check className="h-3 w-3" />
                </div>
              )}

              {/* Theme preview widget */}
              <ThemePreview themeId={theme.id} size="medium" />

              {/* Theme name below the widget */}
              <div className="mt-3">
                <h4 className="font-medium text-gray-900 truncate text-sm">
                  {theme.name || theme.id.charAt(0).toUpperCase() + theme.id.slice(1)}
                </h4>
                {theme.isCustom && <p className="text-xs text-gray-500 mt-0.5">Customized</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Section - Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left Panel - Component Selector */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Select Component</h3>
          <div className="grid grid-cols-2 gap-3">
            {components.map(comp => (
              <button
                key={comp.id}
                onClick={() => onComponentChange(comp.id)}
                className={cn(
                  'px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                  selectedComponent === comp.id
                    ? 'bg-primary-600 text-white shadow-md transform scale-105'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                )}
              >
                {comp.name}
              </button>
            ))}
          </div>
        </div>

        {/* Right Panel - Style Editor */}
        <div className="bg-white rounded-lg shadow-sm p-6 overflow-visible">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {components.find(c => c.id === selectedComponent)?.name} Styles
          </h3>

          <div className="space-y-4 max-h-[400px] overflow-y-auto overflow-x-visible pr-2">
            {Object.entries(componentStyles).map(([property, value]) => (
              <ColorPickerPopover
                key={`${selectedComponent}-${property}`}
                label={
                  property.charAt(0).toUpperCase() + property.slice(1).replace(/([A-Z])/g, ' $1')
                }
                color={value as string}
                onChange={color => onColorChange(selectedComponent, property, color)}
              />
            ))}

            {Object.keys(componentStyles).length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No customizable styles for this component</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
